import cx from 'classnames';
import type { ChangeEvent, ComponentPropsWithoutRef, ReactNode } from 'react';

export interface SharedInputProps {
  id: string;
  label?: ReactNode;
  error?: ReactNode;
  description?: ReactNode;
  inputSuffix?: ReactNode;
  mktSelector?: string;
  qaSelector?: string;
  isValid?: boolean;
}

export type TextInputProps = SharedInputProps & {
  bare?: boolean;
  onChange?: (value: any) => void;
  matchPattern?: RegExp;
} & (
    | ({ isTextArea: true } & Omit<
        ComponentPropsWithoutRef<'textarea'>,
        'onChange'
      >)
    | ({ isTextArea?: false } & Omit<
        ComponentPropsWithoutRef<'input'>,
        'onChange'
      >)
  );

export default function TextInput({
  label,
  id,
  error,
  matchPattern,
  description,
  value,
  onChange = () => {},
  inputSuffix,
  className,
  mktSelector,
  qaSelector,
  isValid,
  bare = false,
  ...props
}: TextInputProps) {
  let input: JSX.Element;
  function handleChange(
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) {
    if (matchPattern && !event.target.value.match(matchPattern)) {
      return;
    }
    onChange(event.target.value);
  }
  if (props.isTextArea === true) {
    input = (
      <textarea
        className={cx('form-control', className, {
          'is-invalid': isValid !== false,
        })}
        id={id}
        value={value}
        onChange={handleChange}
        {...props}
      />
    );
  } else {
    input = (
      <Input
        type={props.type}
        className={className}
        error={error}
        id={id}
        value={value}
        onChange={handleChange}
        inputSuffix={inputSuffix}
        isValid={isValid !== false}
        data-mkt-selector={mktSelector}
        data-qa-selector={qaSelector || mktSelector}
        {...props}
      />
    );
  }

  if (bare === true) {
    return input;
  }

  return (
    <div className="form-group">
      {label && (
        <label className="font-weight-bold" htmlFor={id}>
          {label}
        </label>
      )}

      {input}

      {description && (
        <small className="form-text text-muted text-right">{description}</small>
      )}
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
}

export function Input({
  type,
  className,
  error: _error,
  id,
  value,
  onChange,
  inputSuffix,
  isValid,
  ...props
}: SharedInputProps & ComponentPropsWithoutRef<'input'>) {
  const input = (
    <input
      type={type}
      className={cx('form-control', className, {
        'is-invalid': !isValid,
      })}
      id={id}
      value={value}
      onChange={onChange}
      {...props}
    />
  );
  return inputSuffix ? (
    <div className="input-suffix__container">
      {input}
      {inputSuffix ? <span className="input-suffix">{inputSuffix}</span> : null}
    </div>
  ) : (
    input
  );
}
